import moment from 'moment';
import {
  Appointment,
  AssignedPerson,
  createStartOfDayWithTimeZone,
  ICalendarEventResponse,
  IShowroomShift,
  IStaffLeave,
  ShowroomTiming,
} from '../..';
import {
  activityTypeToIconMapper,
  IEventMapDetail,
} from '../../../containers/leads/components/activities/add-activity';
import { combineDateAndTime } from '../../../utils/CombineDateAndTime';
import { ICalendarFilter } from '../../hooks/useCalendarContext';
import {
  IBigCalendarResource,
  IBigCalendarEvent,
  IShowrrom,
} from './big-calendar-component';

export const parseAssigneesBigCalendarResources = (
  persons: AssignedPerson[],
  showroomIds?: string[]
) => {
  return (
    persons?.map((p) => {
      const e: IBigCalendarResource = {
        resourceId: p._id ?? '',
        resourceTitle: (p.knownAs || p.name) ?? '',
        resourceType: 'staff',
        showroom: getCorrespondingshowroom(p.showrooms, showroomIds),
        avatar: p.avatar,
      };
      return e;
    }) ?? []
  );
};

const getCorrespondingshowroom = (
  resourceShowrooms?: IShowrrom[],
  filterShowroomIds?: string[]
) => {
  if ((resourceShowrooms?.length ?? 0) === 0) {
    return;
  }
  if ((filterShowroomIds?.length ?? 0) === 0) {
    return resourceShowrooms?.[0];
  }
  if ((filterShowroomIds?.length ?? 0) === 1) {
    return resourceShowrooms?.find((p) => p._id === filterShowroomIds?.[0]);
  }
};

export const parseStaffLeaves = (leaves: IStaffLeave[]) => {
  const staffLeave: { [id: string]: IStaffLeave[] } = {};
  leaves?.forEach((p) => {
    if (p.assignedPerson?._id != null) {
      if (staffLeave[p.assignedPerson?._id] == null) {
        staffLeave[p.assignedPerson?._id] = [];
      }
      staffLeave[p.assignedPerson?._id].push(p);
    }
  });
  return staffLeave;
};

export const parseShowroomShifts = (
  timings: ShowroomTiming[],
  filter: ICalendarFilter
) => {
  if (filter?.showroomIds?.length === 1) {
    const showroomtimings: { [id: string]: IShowroomShift[] } = {};
    const date = createStartOfDayWithTimeZone(filter?.fromDate);
    timings?.forEach((p) => {
      if (p.showroom != null) {
        showroomtimings[p.showroom._id] =
          p.showroom.shifts?.map((q) => {
            return {
              _id: q._id,
              start: combineDateAndTime(date, q.start),
              end: combineDateAndTime(date, q.end),
            };
          }) ?? [];
      }
    });
    return showroomtimings;
  }
  return {};
};

export const parseCalendarEventsToBigCalendarEvents = (
  appointments: Appointment[]
) => {
  return (
    appointments?.map((p) => {
      const e: IBigCalendarEvent = {
        id: p._id,
        title: p.title,
        start: moment(p.startDate).toDate(),
        end: moment(p.endDate ?? p.startDate).toDate(),
        resourceId: p.assignedPerson?._id ?? '',
        displayDetails: getActivityDisplayDetails(p.activityType),
        activityType: p.activityType,
        appointment: p,
      };
      return e;
    }) ?? []
  );
};

export const getActivityDisplayDetails = (type: string): IEventMapDetail => {
  const item = activityTypeToIconMapper[type];
  if (item != null) {
    return item;
  }
  return {
    icon: '',
    text: `Unhandled type(${type})`,
    color: 'f77979',
    isDraggable: false,
    isSingleDayEvent: true,
    className: 'unhandled',
  };
};

export const parseVehiclesBigCalendarResources = (vehicles: any[]) => {
  return (
    vehicles?.map((p) => {
      const e: IBigCalendarResource = {
        resourceId: p._id ?? '',
        resourceTitle: p.vehicleModel?.modelCode ?? '',
        subText: `${p.registrationNo} - ${p.vehicleVariant?.variantCode}`,
        resourceType: 'vehicle',
        showroom: {
          _id: p.showroom?._id ?? '',
          title: p.showroom?.title ?? '',
        },
        vehicleModel: p.vehicleModel,
        vehicleVariant: p.vehicleVariant,
        registrationNo: p.registrationNo,
      };
      return e;
    }) ?? []
  );
};

export const parseVehicleEventsToBigCalendarEvents = (
  appointments: Appointment[]
) => {
  return (
    appointments?.map((p) => {
      const e: IBigCalendarEvent = {
        id: p._id,
        title: p.title,
        start: moment(p.startDate).toDate(),
        end: moment(p.endDate ?? p.startDate).toDate(),
        resourceId: p.vehicle?._id ?? '',
        displayDetails: getActivityDisplayDetails(p.activityType),
        activityType: p.activityType,
        appointment: p,
      };
      return e;
    }) ?? []
  );
};

export const getMinimumTimeAndMaximumTime = (
  response: ICalendarEventResponse
): IMinMaxDate => {
  const appointmentStartTiming = response?.appointments?.map(
    (p) => p.startDate
  );

  const appointmentEndTiming = response?.appointments?.map(
    (p) => p?.endDate ?? new Date()
  );
  const min = appointmentStartTiming.map((d) => moment(d));
  const max = appointmentEndTiming.map((d) => moment(d));
  return {
    max: moment.max(max).toDate(),
    min: moment.min(min).toDate(),
  };
};

export interface IMinMaxDate {
  min: Date;
  max: Date;
}
